import { MatchTargetKey } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from '../services/matching.service'

/**
 * Prefix marking a column whose value is computed from the match graph rather than read off the
 * row. The ColDef factory wires these to {@link calcUnitValue}. Relocated here from the (now
 * fixture-only) clearwater.catalog so the calc: pipeline no longer depends on a static catalog.
 */
export const CALC_FIELD_PREFIX = 'calc:'

/** Decimal places the unit differences are rounded to, matching the prototype. */
export const UNIT_DIFF_PRECISION = 4

/** The Clearwater field the differences are measured against. */
const CLEARWATER_UNITS_FIELD = 'currentUnits'

/** The four computed columns in the Clearwater `External` group. */
export type CalcField =
  | 'custUnits'
  | 'tradUnits'
  | 'custUnitsDiff'
  | 'tradUnitsDiff'

const TARGET_OF: Record<CalcField, MatchTargetKey> = {
  custUnits: 'cust',
  tradUnits: 'trad',
  custUnitsDiff: 'cust',
  tradUnitsDiff: 'trad',
}

const IS_DIFFERENCE: Record<CalcField, boolean> = {
  custUnits: false,
  tradUnits: false,
  custUnitsDiff: true,
  tradUnitsDiff: true,
}

const round = (value: number): number =>
  Number(value.toFixed(UNIT_DIFF_PRECISION))

const numberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && !isNaN(value) ? value : null

/** Strip the `calc:` marker off a catalog field, or return null if it isn't a computed column. */
export const calcFieldOf = (field: string): CalcField | null =>
  field.startsWith(CALC_FIELD_PREFIX)
    ? (field.slice(CALC_FIELD_PREFIX.length) as CalcField)
    : null

/**
 * Units of the Prometheus positions matched to this Clearwater position, and the difference against
 * Clearwater Current Units. Multiple matches from one sub-source sum.
 *
 * Both sides are nullable and null propagates: an unmatched row, a row with no links from that
 * sub-source, or a Clearwater row with no Current Units all yield an empty cell rather than a
 * misleading zero.
 */
export function calcUnitValue(
  field: CalcField,
  row: WorkbenchRow,
  matching: MatchingService,
): number | null {
  const external = matching.sumLinked(
    row,
    TARGET_OF[field],
    linked => linked.units,
  )
  if (!IS_DIFFERENCE[field]) return external

  const clearwaterUnits = numberOrNull(row.fields[CLEARWATER_UNITS_FIELD])
  if (external == null || clearwaterUnits == null) return null
  return round(clearwaterUnits - external)
}
